// src/components/common/ConfirmDialogActions.js
//
// Footer (button row) of ConfirmDialog, extracted 2026-08-19 to keep the
// dialog under the 500-line cap when the opt-in action beat landed. Two
// mutually-exclusive states in ONE slot (stable-layout rule): the ordinary
// [Cancel][Secondary?][Confirm] row at rest, the progress→success
// ModalActionBeat once a beat-mode confirm starts.

import { useIntl } from 'react-intl';
import { typedConfirmActionsRow } from '../../ui/confirmDialogStyles';
import { foundation } from '../../ui/tokens';
import ConfirmMorphActions from './ConfirmMorphActions';
import ActionBeatFill from './actionBeat/ActionBeatFill';
import useOverlayFooter from './overlay/useOverlayFooter';
import { foldingLinkStyle } from './actionBeat/foldingLinkStyle';

const { spacePx } = foundation;

export default function ConfirmDialogActions({
  hasActionBeat,
  beat,
  actionBeat,
  /** resolveConfirmBeat's kind: null | 'fill' (KAN-1078: the one write beat). */
  beatKind,
  /** cfg.fill?.tone from resolveConfirmBeat — only meaningful when beatKind is 'fill'. */
  fillTone,
  reduced,
  tone,
  hasTypedField,
  cancelLabel,
  guardedCancel,
  hasSecondary,
  secondaryAction,
  runSecondary,
  confirmLabel,
  canConfirm,
  runConfirm,
  busy,
  confirmingLabel,
  /** Two-step morph gate (absent for every existing caller). */
  morph,
  /** Task 2 — ONE checking state (WAVE 1 · ENGINE). Owns the whole row. */
  checking = false,
  checkingLabel,
}) {
  const intl = useIntl();
  // The ONE footer (KAN-948): 38px web / 44px touch, Cancel outlined on the left, main action on
  // the right, a third choice as a text link ABOVE the row. ActionBeatFill takes the same styles
  // as its defaults, so the box is identical before and after a beat.
  const footer = useOverlayFooter();
  // The link's TEXT sits the same distance from the card above and the buttons below (~16 web, ~21 touch,
  // measured on the real build): the body already leaves ~32px above the link box, so pull it up 24 and keep
  // an 8px gap below; the link box's own padding supplies the rest on both sides.
  const linkStackMarginTop = hasTypedField
    ? typedConfirmActionsRow({ afterInput: true }).marginTop // typed-confirm dialogs keep their old spacing
    : -24;
  const linkStackStyle = hasTypedField ? footer.stack : { ...footer.stack, gap: spacePx[2] };

  // A pre-check in flight owns the row outright: no gate, no morph, no
  // fill/ring beat exists yet (the caller doesn't know the final shape until
  // `prepare` resolves) — just [Cancel][disabled Confirm]. Cancel/Esc/scrim
  // stay live (guardedCancel already no-ops while busy/beat.isWorking, but
  // neither is true here). Same slot every branch below returns into, so the
  // swap to the final gate on `checking` flipping false is one re-render.
  if (checking) {
    const checkingConfirmBase = tone === 'danger' ? footer.danger : footer.primary;
    return (
      <div style={typedConfirmActionsRow({ afterInput: hasTypedField })}>
        {cancelLabel ? (
          <button type="button" style={footer.cancel} onClick={guardedCancel} data-testid="confirm-dialog-cancel-button">
            {cancelLabel}
          </button>
        ) : null}
        {confirmLabel ? (
          <button
            type="button"
            style={{ ...checkingConfirmBase, opacity: 0.7, cursor: 'not-allowed' }}
            disabled
            data-testid="confirm-dialog-confirm-button"
          >
            {checkingLabel || intl.formatMessage({ id: 'common.checking', defaultMessage: 'Checking…' })}
          </button>
        ) : null}
      </div>
    );
  }

  // kind:'fill' + a morph gate → the beat plays INSIDE the armed capsule
  // itself (ConfirmMorphActions' `beat` prop), in ALL phases — never a
  // separate sweep bar. Must be checked before the generic
  // `beat.isWorking` branch below, which would otherwise intercept it.
  const fillOwnsMorphCapsule = hasActionBeat && beatKind === 'fill' && Boolean(morph) && !hasSecondary;
  if (fillOwnsMorphCapsule) {
    return (
      <ConfirmMorphActions
        armed={morph.armed}
        onActivate={morph.activate}
        onCancel={guardedCancel}
        ctaLabel={confirmLabel}
        step2Label={morph.step2Label}
        cancelLabel={cancelLabel}
        tone={tone}
        disabled={!morph.enabled}
        busy={busy}
        hasTypedField={hasTypedField}
        reduced={morph.reduced}
        beat={{
          phase: beat.phase,
          tone: fillTone,
          workingLabel: actionBeat.workingLabel,
          successLabel: actionBeat.successLabel,
        }}
      />
    );
  }

  // FILL owns the whole row in every phase (idle -> working -> success) (KAN-1078:
  // the ONE write beat, ring and sweep bar are gone). A third choice is a text link
  // ABOVE the row; it folds away with Cancel once the beat starts. A morph gate WITHOUT
  // a secondary plays the beat inside its capsule (above); with one, the ordinary row.
  const fillOwnsRow = hasActionBeat && beatKind === 'fill' && (!morph || hasSecondary);
  if (fillOwnsRow) {
    const baseFillRow = typedConfirmActionsRow({ afterInput: hasTypedField });
    const fillRow = (
      <div style={hasSecondary ? { ...baseFillRow, marginTop: 0 } : baseFillRow}>
        <ActionBeatFill
          phase={beat.phase}
          tone={fillTone}
          buttonTone={tone === 'danger' ? 'danger' : 'primary'}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          onConfirm={runConfirm}
          onCancel={guardedCancel}
          canConfirm={canConfirm}
          busy={busy}
          workingLabel={actionBeat.workingLabel}
          successLabel={actionBeat.successLabel}
          reduced={reduced}
        />
      </div>
    );
    if (!hasSecondary) return fillRow;
    const beatActive = beat.phase !== 'idle';
    return (
      <div style={{ ...linkStackStyle, marginTop: linkStackMarginTop }}>
        <div style={foldingLinkStyle(beatActive, reduced)} aria-hidden={beatActive || undefined}>
          <button
            type="button"
            style={busy || beatActive ? { ...footer.link, opacity: 0.55, cursor: 'not-allowed' } : footer.link}
            onClick={busy || beatActive ? undefined : runSecondary}
            disabled={busy || beatActive}
            tabIndex={beatActive ? -1 : undefined}
            data-testid="confirm-dialog-secondary-button"
          >
            {secondaryAction}
          </button>
        </div>
        {fillRow}
      </div>
    );
  }

  // Two-step morph owns the whole row when it's on. A secondary action has no
  // place in a gate whose entire premise is one primary that changes meaning,
  // so a caller asking for both falls back to the ordinary row rather than
  // silently dropping the third button.
  if (morph && confirmLabel && !hasSecondary) {
    return (
      <ConfirmMorphActions
        armed={morph.armed}
        onActivate={morph.activate}
        onCancel={guardedCancel}
        ctaLabel={confirmLabel}
        step2Label={morph.step2Label}
        cancelLabel={cancelLabel}
        tone={tone}
        disabled={!morph.enabled}
        busy={busy}
        hasTypedField={hasTypedField}
        reduced={morph.reduced}
      />
    );
  }

  const confirmStyle = tone === 'danger' ? footer.danger : footer.primary;
  const disabledConfirmStyle = { ...confirmStyle, opacity: 0.45, cursor: 'not-allowed' };
  const busyConfirmStyle = { ...confirmStyle, opacity: 0.7, cursor: 'not-allowed' };
  const busyCancelStyle = { ...footer.cancel, opacity: 0.55, cursor: 'not-allowed' };

  const baseRowStyle = typedConfirmActionsRow({ afterInput: hasTypedField });
  const buttonRow = (
    <div style={hasSecondary ? { ...baseRowStyle, marginTop: 0 } : baseRowStyle}>
      {cancelLabel ? (
        <button
          type="button"
          style={busy ? busyCancelStyle : footer.cancel}
          onClick={busy ? undefined : guardedCancel}
          disabled={busy}
          data-testid="confirm-dialog-cancel-button"
        >
          {cancelLabel}
        </button>
      ) : null}
      {confirmLabel ? (
        <button
          type="button"
          style={busy ? busyConfirmStyle : (canConfirm ? confirmStyle : disabledConfirmStyle)}
          onClick={busy ? undefined : (canConfirm ? runConfirm : undefined)}
          disabled={!canConfirm || busy}
          data-testid="confirm-dialog-confirm-button"
        >
          {busy
            ? (confirmingLabel
              || intl.formatMessage({ id: 'common.working', defaultMessage: 'Working…' }))
            : confirmLabel}
        </button>
      ) : null}
    </div>
  );

  if (!hasSecondary) return buttonRow;

  // A third choice is a text LINK above the row, never a third button (KAN-948).
  return (
    <div style={{ ...linkStackStyle, marginTop: linkStackMarginTop }}>
      <button
        type="button"
        style={busy ? { ...footer.link, opacity: 0.55, cursor: 'not-allowed' } : footer.link}
        onClick={busy ? undefined : runSecondary}
        disabled={busy}
        data-testid="confirm-dialog-secondary-button"
      >
        {secondaryAction}
      </button>
      {buttonRow}
    </div>
  );
}
